import { Injectable } from '@angular/core';
import { InventoryQueryParamsService } from 'src/app/models/interfaces/inventory-query-params-service';
import { InventoryGroup, InventoryGroupName } from 'src/app/models/results/inventory-group';
import { InventoryQueryParams } from 'src/app/models/results/inventory-query-params';
import { InventoryUserType } from 'src/app/models/results/inventory-user-type';
import { WizardOption } from 'src/app/models/wizard/wizard-option';
import { constants } from 'src/environments/constants';
import { ConfigService } from '../../config/config.service';

@Injectable({
  providedIn: 'root'
})
export class EnterpriseInventoryQueryParamsService implements InventoryQueryParamsService {

  constructor(private confSvc: ConfigService) { }

  setFields(queryParams: InventoryQueryParams, needToRunOptions: WizardOption[], useCaseOption: WizardOption, brandOptions: WizardOption[]): void {
    this.setUserType(queryParams, useCaseOption);
    this.setGroups(queryParams, needToRunOptions);
    this.setBrands(queryParams, brandOptions);

    queryParams.nonParallelsOptionSelected = needToRunOptions.some(option => option.title !== this.confSvc.getString("needToRunParallelsOptionEnterpriseTitle"));
  }

  private setUserType(queryParams: InventoryQueryParams, useCaseOption: WizardOption): void {

    switch(useCaseOption?.title) {
      case this.confSvc.getString("useCaseFrontlineWorkerTitle"):
        queryParams.userType = Object.assign(new InventoryUserType(), {
          name: this.confSvc.getString("useCaseFrontlineWorkerTitle"),
          allowedFormFactors: [
            constants.CLAMSHELL,
            constants.CONVERTIBLE,
            constants.CHROMEBOX,
            constants.CHROMEBASE,
            constants.TABLET
          ],
          minimumScreenSize: this.confSvc.getNum("frontlineWorkerMinScreenSize"),
          maximumScreenSize: this.confSvc.getNum("frontlineWorkerMaxScreenSize")
        });
        break;
      case this.confSvc.getString("useCaseContactCenterTitle"):
        // NOTE: this user type follows the same logic for now as Information Worker which is why information worker's spec requirements are used
        queryParams.userType = Object.assign(new InventoryUserType(), {
          name: this.confSvc.getString("useCaseContactCenterTitle"),
          allowedFormFactors: [
            constants.CLAMSHELL,
            constants.CONVERTIBLE,
            constants.CHROMEBOX,
            constants.CHROMEBASE
          ],
          minimumScreenSize: this.confSvc.getNum("informationWorkerMinScreenSize"),
          maximumScreenSize: this.confSvc.getNum("informationWorkerMaxScreenSize"),
          minimumStorage: this.confSvc.getNum("informationWorkerMinStorage"),
          requiresFingerprintSensor: this.confSvc.getBool("informationWorkerRequiresFingerprintSensor"),
          requiresPrivacyScreen: this.confSvc.getBool("informationWorkerRequiresPrivacyScreen"),
          requiresBacklitKeyboard: this.confSvc.getBool("informationWorkerRequiresBacklitKeyboard")
        });
        break;
      case this.confSvc.getString("useCaseInformationWorkerTitle"):
        queryParams.userType = Object.assign(new InventoryUserType(), {
          name: this.confSvc.getString("useCaseInformationWorkerTitle"),
          allowedFormFactors: [
            constants.CLAMSHELL,
            constants.CONVERTIBLE,
            constants.CHROMEBOX,
            constants.CHROMEBASE
          ],
          minimumScreenSize: this.confSvc.getNum("informationWorkerMinScreenSize"),
          maximumScreenSize: this.confSvc.getNum("informationWorkerMaxScreenSize"),
          minimumStorage: this.confSvc.getNum("informationWorkerMinStorage"),
          requiresFingerprintSensor: this.confSvc.getBool("informationWorkerRequiresFingerprintSensor"),
          requiresPrivacyScreen: this.confSvc.getBool("informationWorkerRequiresPrivacyScreen"),
          requiresBacklitKeyboard: this.confSvc.getBool("informationWorkerRequiresBacklitKeyboard")
        });
        break;
      case this.confSvc.getString("useCaseExecutiveWorkerTitle"):
        queryParams.userType = Object.assign(new InventoryUserType(), {
          name: this.confSvc.getString("useCaseExecutiveWorkerTitle"),
          allowedFormFactors: [
            constants.CONVERTIBLE
          ],
          minimumScreenSize: this.confSvc.getNum("executiveMobileWorkerMinScreenSize"),
          maximumScreenSize: this.confSvc.getNum("executiveMobileWorkerMaxScreenSize"),
          requiresFingerprintSensor: this.confSvc.getBool("executiveMobileWorkerRequiresFingerprintSensor"),
          requiresPrivacyScreen: this.confSvc.getBool("executiveMobileWorkerRequiresPrivacyScreen"),
          requiresBacklitKeyboard: this.confSvc.getBool("executiveMobileWorkerRequiresBacklitKeyboard"),
          requiresStylus: this.confSvc.getBool("executiveMobileWorkerRequiresStylus")
        });
        break;
        case this.confSvc.getString("useCaseKioskTitle"):
        queryParams.userType = Object.assign(new InventoryUserType(), {
          name: this.confSvc.getString("useCaseKioskTitle"),
          allowedFormFactors: [
            constants.CLAMSHELL,
            constants.CONVERTIBLE,
            constants.CHROMEBOX,
            constants.CHROMEBASE,
            constants.TABLET
          ],
          minimumScreenSize: this.confSvc.getNum("kioskMinScreenSize"),
          maximumScreenSize: this.confSvc.getNum("kioskMaxScreenSize")
        });
        break;
      default:
        break;
    }
  }

  private setGroups(queryParams: InventoryQueryParams, needToRunOptions: WizardOption[]): void {
    const needToRunOptionTitles = needToRunOptions.map(option => option.title);

    const groups = this.getGroups(needToRunOptionTitles);

    groups.forEach(group => {
      if (this.needToRunOptionForGroupWasSelected(group, needToRunOptionTitles)) {
        queryParams.groups.push(group);
      }
    });

    this.sortGroupsByPriority(queryParams);
  }

  public getGroups(selectedNeedToRunOptionTitles: string[]): InventoryGroup[] {
    return [
      Object.assign(new InventoryGroup(), {
        name: InventoryGroupName.BrowsingAndSingleTasks,
        minimumRAM: this.confSvc.getNum("browsingAndSingleTasksMinRAM"),
        allowedCPUs: [
          this.confSvc.getString("intelCeleron"),
          this.confSvc.getString("amdA6"),
          this.confSvc.getString("amdRyzen"),
          this.confSvc.getString("mediaTekMT8183"),
          this.confSvc.getString("inteli3"),
          this.confSvc.getString("inteli5"),
          this.confSvc.getString("inteli7"),
          this.confSvc.getString("intelPentium"),
          this.confSvc.getString("qualcomm7c")
        ],
        priority: this.confSvc.getNum("browsingAndSingleTasksPriority"),
        needToRunOptions: [this.confSvc.getString("needToRunBrowsingOptionEnterpriseTitle")],
        userTypeNames: [
          this.confSvc.getString("useCaseFrontlineWorkerTitle"),
          this.confSvc.getString("useCaseKioskTitle")
        ]
      }),
      Object.assign(new InventoryGroup(), {
        name: InventoryGroupName.Collaborating,
        minimumRAM: this.confSvc.getNum("collaboratingMinRAM"),
        allowedCPUs: [
          this.confSvc.getString("intelPentium"),
          this.confSvc.getString("inteli3"),
          this.confSvc.getString("qualcomm7c"),
          this.confSvc.getString("amdRyzen"),
          this.confSvc.getString("inteli5"),
          this.confSvc.getString("inteli7")
        ],
        priority: this.confSvc.getNum("collaboratingPriority"),
        needToRunOptions: [
          this.confSvc.getString("needToRunConferencingOptionEnterpriseTitle"),
          this.confSvc.getString("needToRunProductivityOptionEnterpriseTitle")
        ],
        userTypeNames: [
          this.confSvc.getString("useCaseFrontlineWorkerTitle"),
          this.confSvc.getString("useCaseKioskTitle"),
          this.confSvc.getString("useCaseContactCenterTitle"),
          this.confSvc.getString("useCaseInformationWorkerTitle")
        ]
      }),
      Object.assign(new InventoryGroup(), {
        name: InventoryGroupName.AppsAndVirtualization,
        minimumRAM: this.confSvc.getNum("appsAndVirtualizationMinRAM"),
        allowedCPUs: [
          this.confSvc.getString("inteli3"),
          this.confSvc.getString("inteli5"),
          this.confSvc.getString("inteli7"),
          this.confSvc.getString("amdRyzen")
        ],
        priority: this.confSvc.getNum("appsAndVirtualizationPriority"),
        needToRunOptions: [
          this.confSvc.getString("needToRunGooglePlayOptionEnterpriseTitle"),
          this.confSvc.getString("needToRunVirtualizationOptionEnterpriseTitle")
        ],
        userTypeNames: [
          this.confSvc.getString("useCaseFrontlineWorkerTitle"),
          this.confSvc.getString("useCaseKioskTitle"),
          this.confSvc.getString("useCaseContactCenterTitle"),
          this.confSvc.getString("useCaseInformationWorkerTitle"),
          this.confSvc.getString("useCaseExecutiveWorkerTitle")
        ]
      }),
      Object.assign(new InventoryGroup(), {
        name: InventoryGroupName.PowerUsage,
        minimumRAM: this.confSvc.getNum("powerUsageMinRAM"),
        allowedCPUs: [
          this.confSvc.getString("inteli5"),
          this.confSvc.getString("inteli7"),
          this.confSvc.getString("amdRyzen5"),
          this.confSvc.getString("amdRyzen7")
        ],
        priority: this.confSvc.getNum("powerUsagePriority"),
        needToRunOptions: [
          this.confSvc.getString("needToRunLinuxOptionEnterpriseTitle"),
          this.confSvc.getString("needToRunParallelsOptionEnterpriseTitle")
        ],
        userTypeNames: [
          this.confSvc.getString("useCaseContactCenterTitle"),
          this.confSvc.getString("useCaseInformationWorkerTitle"),
          this.confSvc.getString("useCaseExecutiveWorkerTitle")
        ],
        parallelsSelected: this.parallelsOptionWasSelected(selectedNeedToRunOptionTitles)
      })
    ];
  }

  private parallelsOptionWasSelected(selectedNeedToRunOptionTitles: string[]): boolean {
    return selectedNeedToRunOptionTitles.some(title => title === this.confSvc.getString("needToRunParallelsOptionEnterpriseTitle"));
  }

  private needToRunOptionForGroupWasSelected(group: InventoryGroup, selectedNeedToRunOptionTitles: string[]): boolean {
    return group.needToRunOptions.some(option => selectedNeedToRunOptionTitles.includes(option));
  }

  private sortGroupsByPriority(queryParams: InventoryQueryParams): void {
    queryParams.groups.sort((a, b) => (a.priority < b.priority) ? 1 : -1);
  }

  private setBrands(queryParams: InventoryQueryParams, brandOptions: WizardOption[]): void {
    if (brandOptions.length && (brandOptions[0].title !== this.confSvc.getString("brandNoPreferenceOptionTitle"))) {
      queryParams.brands = brandOptions.map(option => this.getDataSetBrandName(option.title));
    }
  }

  private getDataSetBrandName(displayBrandName: string): string {
    return this.confSvc.getRelatedFieldFromConfig(displayBrandName, "Display", "DataSource");
  }
}
